import {
  EMAIL_DISCLAIMER_MERGE_TAG,
  EMAIL_SIGNATURE_MERGE_TAG
} from "components/email-branding/email-branding.utils";
import { replaceMergeTagsWithValues } from "components/email-form/email-form-merge-tags.utils";
import { getLeadMergeTagValuesMap } from "components/word-processor-field/merge-tags/merge-tags-values/lead-contact-values.utils";
import type { ContactFull } from "services/suno-comms/types/lead-contact.type";

export interface EmailBuilderMergeTag {
  name: string;
  value: string;
  sample?: string;
}

export type EmailBuilderMergeTags = Record<string, EmailBuilderMergeTag>;

type BuildLeadsEmailBuilderMergeTagsOptions = {
  /** When true, picker inserts resolved contact values instead of SUNO placeholders. */
  insertValues?: boolean;
};

const LEADS_EMAIL_BUILDER_TAG_DEFINITIONS: Array<{
  id: string;
  name: string;
  token: string;
}> = [
  { id: "lead_fullname", name: "Lead Full Name", token: "lead_fullname" },
  { id: "lead_first_name", name: "Lead First Name", token: "lead_first_name" },
  { id: "lead_last_name", name: "Lead Last Name", token: "lead_last_name" },
  { id: "lead_birthdate", name: "Lead Birth Date", token: "lead_birthdate" },
  { id: "lead_email", name: "Lead Email", token: "lead_email" },
  { id: "lead_phone", name: "Lead Phone", token: "lead_phone" },
  { id: "lead_zip_code", name: "Lead Zip Code", token: "lead_zip_code" },
  {
    id: "lead_service_interests",
    name: "Lead Service Interests",
    token: "lead_service_interests"
  },
  { id: "lead_page_url", name: "Lead Page Url", token: "page_url" },
  { id: "lead_form_title", name: "Lead Form Title", token: "form_title" }
];

export const buildLeadsEmailBuilderMergeTags = (
  leadContact?: ContactFull,
  options?: BuildLeadsEmailBuilderMergeTagsOptions
): EmailBuilderMergeTags => {
  const insertValues = Boolean(options?.insertValues && leadContact);
  const valuesMap = insertValues ? getLeadMergeTagValuesMap(leadContact) : {};

  return LEADS_EMAIL_BUILDER_TAG_DEFINITIONS.reduce<EmailBuilderMergeTags>(
    (acc, { id, name, token }) => {
      const resolvedValue = valuesMap[token] ?? "";
      const placeholder = `!--SUNO:${token}--`;

      acc[id] = {
        name,
        value: insertValues && resolvedValue ? resolvedValue : placeholder,
        sample: resolvedValue || `{${token}}`
      };
      return acc;
    },
    {}
  );
};

/** Replaces SUNO merge-tag placeholders inside an Unlayer design JSON payload. */
export const resolveMergeTagsInUnlayerDesign = (
  design: UnlayerDesign,
  valuesMap: Record<string, string>
): UnlayerDesign => {
  try {
    const resolvedJson = replaceMergeTagsWithValues(
      JSON.stringify(design),
      valuesMap
    );
    return JSON.parse(resolvedJson) as UnlayerDesign;
  } catch (err) {
    console.error(
      "Failed to resolve merge tags in design; loading unresolved design",
      err
    );
    return design;
  }
};

const patient_first_name = {
  name: "Patient First Name",
  value: "!--SUNO:patient_first_name--",
  sample: "{patient_first_name}"
};

const patient_preferred_name = {
  name: "Patient Preferred Name",
  value: "!--SUNO:patient_preferred_name--",
  sample: "{patient_preferred_name}"
};

const practice_name = {
  name: "Practice Name",
  value: "!--SUNO:practice_name--",
  sample: "{practice_name}"
};

const clinic_name = {
  name: "Clinic Name",
  value: "!--SUNO:clinic_name--",
  sample: "{clinic_name}"
};

const clinic_display_name = {
  name: "Clinic Display Name",
  value: "!--SUNO:clinic_display_name--",
  sample: "{clinic_display_name}"
};

const clinic_phone = {
  name: "Clinic Phone",
  value: "!--SUNO:clinic_phone--",
  sample: "{clinic_phone}"
};

const clinic_email = {
  name: "Clinic Email",
  value: "!--SUNO:clinic_email--",
  sample: "{clinic_email}"
};

const email_signature = {
  name: "Email Signature",
  value: `!--SUNO:${EMAIL_SIGNATURE_MERGE_TAG}--`,
  sample: `{${EMAIL_SIGNATURE_MERGE_TAG}}`
};

const email_disclaimer = {
  name: "Email Disclaimer",
  value: `!--SUNO:${EMAIL_DISCLAIMER_MERGE_TAG}--`,
  sample: `{${EMAIL_DISCLAIMER_MERGE_TAG}}`
};

const patient_full_name = {
  name: "Patient Full Name",
  value: "!--SUNO:patient_full_name--",
  sample: "{patient_full_name}"
};

const patient_address = {
  name: "Patient Primary Address",
  value: "!--SUNO:patient_address--",
  sample: "{patient_address}"
};

const patient_pronouns = {
  name: "Patient Pronouns",
  value: "!--SUNO:patient_pronouns--",
  sample: "{patient_pronouns}"
};

const clinic_instructions = {
  name: "Clinic Instructions",
  value: "!--SUNO:clinic_instructions--",
  sample: "{clinic_instructions}"
};

const google_review_url = {
  name: "Clinic Google Review URL",
  value: "!--SUNO:google_review_url--",
  sample: "{google_review_url}"
};

const google_maps_url = {
  name: "Clinic Google Maps URL",
  value: "!--SUNO:google_maps_url--",
  sample: "{google_maps_url}"
};

const clinic_address = {
  name: "Clinic Address",
  value: "!--SUNO:clinic_address--",
  sample: "{clinic_address}"
};

const doctor_title = {
  name: "Doctor Title",
  value: "!--SUNO:doctor_title--",
  sample: "{doctor_title}"
};

const doctor_first_name = {
  name: "Doctor First Name",
  value: "!--SUNO:doctor_first_name--",
  sample: "{doctor_first_name}"
};

const doctor_last_name = {
  name: "Doctor Last Name",
  value: "!--SUNO:doctor_last_name--",
  sample: "{doctor_last_name}"
};

const doctor_full_name = {
  name: "Doctor Full Name",
  value: "!--SUNO:doctor_full_name--",
  sample: "{doctor_full_name}"
};

const reminder_confirmation_url = {
  name: "Reminder Confirmation URL",
  value: "!--SUNO:reminder_confirmation_url--",
  sample: "{reminder_confirmation_url}"
};

const private_feedback_request_url = {
  name: "Private Feedback URL",
  value: "!--SUNO:private_feedback_request_url--",
  sample: "{private_feedback_request_url}"
};

const appointment_time = {
  name: "Appointment Time",
  value: "!--SUNO:time--",
  sample: "{time}"
};

const appointment_date = {
  name: "Appointment Date",
  value: "!--SUNO:date--",
  sample: "{date}"
};

const appointment_prev_date = {
  name: "Appointment Previous Date",
  value: "!--SUNO:prev_date--",
  sample: "{prev_date}"
};

const appointment_prev_time = {
  name: "Appointment Previous Time",
  value: "!--SUNO:prev_time--",
  sample: "{prev_time}"
};

const patient_portal_link = {
  name: "Onboarding Forms URL",
  value: "!--SUNO:patient_portal_link--",
  sample: "{patient_portal_link}"
};

const telehealth_join_url = {
  name: "Telehealth Join URL",
  value: "!--SUNO:telehealth_join_url--",
  sample: "{telehealth_join_url}"
};

const telehealth_test_url = {
  name: "Telehealth Pre-Call Test URL",
  value: "!--SUNO:telehealth_test_url--",
  sample: "{telehealth_test_url}"
};

const google_calendar_url = {
  name: "Google Calendar link",
  value: "!--SUNO:google_calendar_url--",
  sample: "{google_calendar_url}"
};

const outlook_calendar_url = {
  name: "Outlook Calendar link",
  value: "!--SUNO:outlook_calendar_url--",
  sample: "{outlook_calendar_url}"
};

const collection_balance = {
  name: "Collection Balance",
  value: "!--SUNO:balance--",
  sample: "{balance}"
};

const collection_original_balance = {
  name: "Collection Original Balance",
  value: "!--SUNO:original_balance--",
  sample: "{original_balance}"
};

const collection_days_in_collection = {
  name: "Days in Collection",
  value: "!--SUNO:days_in_collection--",
  sample: "{days_in_collection}"
};

const collection_service_date = {
  name: "Date of Service",
  value: "!--SUNO:service_date--",
  sample: "{service_date}"
};

const collection_invoice_number = {
  name: "Invoice Number",
  value: "!--SUNO:invoice_number--",
  sample: "{invoice_number}"
};

const collection_payment_link = {
  name: "Payment Link",
  value: "!--SUNO:payment_link--",
  sample: "{payment_link}"
};

/**
 * The seven tags every patient-scope surface can resolve. Deliberately NOT exported: it is a
 * composition building block for `COLLECTION_*` below, never a set to register on an editor.
 *
 * Registering this narrow set on a surface that can insert a stored Designer Email template is what
 * SN-19526's follow-up was: Unlayer tokenises a token into a chip only when that token is in the set
 * registered on that editor instance (see the ENG-1091 comment in `email-builder.component.tsx`), so
 * a template authored in Settings → Templates against `EXTENDED_*` rendered
 * `!--SUNO:telehealth_join_url--` as literal text on all eight consuming surfaces. Keeping this
 * module-private lets the compiler, rather than a reviewer, catch the next surface that reaches for
 * it.
 */
const CORE_EMAIL_BUILDER_MERGE_TAGS = {
  patient_first_name,
  patient_preferred_name,
  practice_name,
  clinic_name,
  clinic_display_name,
  clinic_phone,
  clinic_email,
  email_signature,
  email_disclaimer
};

export const EXTENDED_EMAIL_BUILDER_MERGE_TAGS = {
  ...CORE_EMAIL_BUILDER_MERGE_TAGS,
  patient_full_name,
  patient_address,
  clinic_instructions,
  google_review_url,
  google_maps_url,
  clinic_address,
  doctor_title,
  doctor_first_name,
  doctor_last_name,
  doctor_full_name,
  reminder_confirmation_url,
  private_feedback_request_url,
  appointment_time,
  appointment_date,
  appointment_prev_date,
  appointment_prev_time,
  patient_portal_link
};

export const EXTENDED_EMAIL_BUILDER_MERGE_TAGS_WITH_PRONOUNS = {
  ...EXTENDED_EMAIL_BUILDER_MERGE_TAGS,
  patient_pronouns
};

/**
 * The EXTENDED sets plus the telehealth and calendar tags, for editors whose templates render
 * against an appointment.
 *
 * Splitting the merge-tag chips matters as much as splitting the special links below, and for a
 * sharper reason: the special link supplies a token to a link's HREF, while the chip supplies one
 * as its VISIBLE TEXT. An unresolved pair collapses to `<a href=""></a>` — zero height — so leaving
 * the chip registered on campaign surfaces left the half that makes the block disappear.
 */
export const APPOINTMENT_EMAIL_BUILDER_MERGE_TAGS = {
  ...EXTENDED_EMAIL_BUILDER_MERGE_TAGS,
  telehealth_join_url,
  telehealth_test_url,
  google_calendar_url,
  outlook_calendar_url
};

export const APPOINTMENT_EMAIL_BUILDER_MERGE_TAGS_WITH_PRONOUNS = {
  ...EXTENDED_EMAIL_BUILDER_MERGE_TAGS_WITH_PRONOUNS,
  telehealth_join_url,
  telehealth_test_url,
  google_calendar_url,
  outlook_calendar_url
};

export const EMAIL_BUILDER_SPECIAL_LINKS: UnlayerSpecialLink[] = [];

export const EXTENDED_EMAIL_BUILDER_SPECIAL_LINKS: UnlayerSpecialLink[] = [
  {
    name: "Merge Tags",
    specialLinks: [
      {
        name: "Clinic Google Review URL",
        href: "!--SUNO:google_review_url--",
        target: "_blank"
      },
      {
        name: "Clinic Google Maps URL",
        href: "!--SUNO:google_maps_url--",
        target: "_blank"
      },
      {
        name: "Reminder Confirmation URL",
        href: "!--SUNO:reminder_confirmation_url--",
        target: "_blank"
      },
      {
        name: "Private Feedback URL",
        href: "!--SUNO:private_feedback_request_url--",
        target: "_blank"
      },
      {
        name: "Onboarding Forms URL",
        href: "!--SUNO:patient_portal_link--",
        target: "_blank"
      }
    ]
  }
];

/**
 * Telehealth links, offered ONLY where the send path actually has an appointment.
 *
 * `telehealth_join_url` resolves from `appointment.zoom_meeting.join_url`, and
 * `ContextManager.update_by_appointment_context` returns early with no appointment in context.
 * Campaign, custom-campaign, bulk-touchpoint and ad-hoc email sends never carry one, so on those
 * surfaces the tag could not resolve for any recipient, ever.
 *
 * That is not a harmless blank. Unlayer writes this token into a link's href AND the merge-tag
 * chip writes it as the link's visible text, so an unresolved pair renders `<a href=""></a>` --
 * zero-height and invisible. Patients received an email with a silent gap where the Zoom link
 * should have been, and nobody could see anything was missing (SN-19526).
 *
 * Same reasoning `COLLECTION_EMAIL_BUILDER_MERGE_TAGS` already records for its own surface.
 */
const TELEHEALTH_SPECIAL_LINKS: UnlayerSpecialLink[] = [
  {
    name: "Telehealth",
    specialLinks: [
      {
        name: "Telehealth Join URL",
        href: "!--SUNO:telehealth_join_url--",
        target: "_blank"
      },
      {
        name: "Telehealth Pre-Call Test URL",
        href: "!--SUNO:telehealth_test_url--",
        target: "_blank"
      }
    ]
  }
];

// SN-19964: the add-to-calendar links are built from the appointment, the same way as telehealth.
const CALENDAR_SPECIAL_LINKS: UnlayerSpecialLink[] = [
  {
    name: "Calendar",
    specialLinks: [
      {
        name: "Google Calendar link",
        href: "!--SUNO:google_calendar_url--",
        target: "_blank"
      },
      {
        name: "Outlook Calendar link",
        href: "!--SUNO:outlook_calendar_url--",
        target: "_blank"
      }
    ]
  }
];

/**
 * For the message-template editors, whose templates render against a real appointment.
 *
 * NOTE: `Reminder Confirmation URL` and `Private Feedback URL` in the EXTENDED set above are
 * appointment-scoped in the same way and have the same blank-on-campaign behaviour. They are left
 * where they are on purpose -- moving them changes behaviour for features outside this fix -- but
 * they belong here eventually.
 */
export const APPOINTMENT_EMAIL_BUILDER_SPECIAL_LINKS: UnlayerSpecialLink[] = [
  ...EXTENDED_EMAIL_BUILDER_SPECIAL_LINKS,
  ...TELEHEALTH_SPECIAL_LINKS,
  ...CALENDAR_SPECIAL_LINKS
];

// Collections stays narrow on purpose: its send path is `outreach_workflow.py`'s
// `ContextManager(patient, clinic)` plus the collections keys — no staff member and no appointment,
// so the doctor/appointment/telehealth tags would render blank on every send.
export const COLLECTION_EMAIL_BUILDER_MERGE_TAGS = {
  ...CORE_EMAIL_BUILDER_MERGE_TAGS,
  collection_balance,
  collection_original_balance,
  collection_days_in_collection,
  collection_service_date,
  collection_invoice_number,
  collection_payment_link
};

export const LEADS_EMAIL_BUILDER_MERGE_TAGS: EmailBuilderMergeTags =
  buildLeadsEmailBuilderMergeTags();

export const LEADS_EMAIL_BUILDER_SPECIAL_LINKS: UnlayerSpecialLink[] = [];

export const COLLECTION_EMAIL_BUILDER_SPECIAL_LINKS: UnlayerSpecialLink[] = [
  {
    name: "Merge Tags",
    specialLinks: [
      {
        name: "Payment Link",
        href: "!--SUNO:payment_link--",
        target: "_blank"
      }
    ]
  }
];
